import React, { useEffect, useLayoutEffect, useCallback, useState, useRef, useMemo } from 'react';
import { useAuth } from '../contexts';
import { useSendMessage } from '../hooks/useSendMessage';
import { proxyImage, getDefaultAvatar } from '@rsmaids/shared/utils/avatarUtils';
import {
  IonPage,IonContent,IonButton,IonIcon,IonGrid,IonRow,IonCol, IonText,
  IonSpinner,IonModal,useIonToast
} from '@ionic/react';
import { callOutline, chevronBackOutline, closeOutline, downloadOutline, shareOutline, ellipsisVertical, personOutline, star, archive, alertCircleOutline, trashOutline, flag, imageOutline, documentOutline, ticketOutline, constructOutline, cardOutline, pricetagOutline, checkmarkCircle, mailOutline } from 'ionicons/icons';
import styles from '../pages/Inbox.module.scss';
import BasePageWithHeader, { TitleCard} from './BasePageWithHeader';
import SheetModal from './SheetModal';
// Extracted services
import { getInitials } from '../services/messageFormatting';
// Extracted hooks
import { useConversationState, useConversationRefs, useMessageLoading, useMessageSubscription } from '../hooks';
import { useConversationMessages } from '../hooks/useConversationMessages';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { msSinceOpen } from '../utils/openTiming';
import { useConversationUnified, mergeConversationItems } from '../hooks/useConversationUnified';
import { useSmsThreads, markSmsRead, SMS_THREAD_PAGE, type SmsMessage } from '../hooks/useSmsInbox';
import { getCachedContactPhone } from '../utils/contactCache';
import { StaffCacheService } from '@rsmaids/shared/services/api/staffCacheService';
import { newId } from '../utils/newId';
import { getWixBusinessProfile } from '@rsmaids/shared/services/api/businessInfo';
// Extracted components
import { TemplatesModal, ChatScroller, ConversationInputFooter, MessageItemRender } from './ConversationComponents';
import type { FooterAttachment } from './chatComponents/ConversationInputFooter';
import { CallMessageItem } from './CallSmsMessageItems';
import { auth } from '../auth/firebase';
import { openIvrDialer } from '../desktop/components/IvrPanel/ivrPanelBus';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

interface Message {
  id?: string; // Message ID from Wix (_id)
  from: string;
  text: string;
  channel: 'chat' | 'email' | 'sms' | 'gmail' | 'outlook' | 'yahoo' | 'other-email' | 'call';
  _createdDate: Date;
  isFromCustomer?: boolean;
  isSystemMessage?: boolean; // Flag for system notifications like "Became a site member"
  deliveryStatus?: 'failed'; // An outgoing text that could not be sent (shown marked, with a retry)
  avatarUrl?: string; // Avatar URL for the sender
  extractedStaffInfo?: { staffName: string; avatarUrl?: string }; // Staff info extracted from message text
  invoicePaidData?: { title: string; textLines?: string[]; buttons?: Array<{ label?: string; launchUrl?: string }>; icon: string };
  newOrderData?: { title: string; textLines?: string[]; buttons?: Array<{ label?: string; launchUrl?: string }>; imageUrl?: string };
  media?: Array<{
    url?: string;
    type: 'image' | 'video' | 'file' | 'audio' | 'icon';
    name?: string;
    iconName?: string; // For icon type, stores the icon identifier
    duration?: number; // For audio type, duration in seconds
  }>;
  // Firestore metadata for staff messages
  senderStaffName?: string;
  senderStaffAvatar?: string;
  senderType?: string;
  senderStaffId?: string;
  targetChannelIds?: string[]; // Wix channel IDs for outbound routing
  // Call/SMS data
  _callData?: {
    callId: string;
    direction: 'inbound' | 'outbound';
    duration: number;
    recordingUrl?: string;
    callerName?: string;
    customerNumber: string;
  };
  _smsData?: {
    direction: 'inbound' | 'outbound';
    customerNumber: string;
  };
}

// ============================================================
// HEADER COMPONENT - Defined outside to prevent remounting
// ============================================================
interface HeaderProps {
  hasMoreOlderMessages: boolean;
  loadingOlderMessages: boolean;
  conversationName: string;
  contactAvatarUrl?: string;
  titleCardRef: React.RefObject<HTMLDivElement | null>;
}

const Header = React.memo(({
  hasMoreOlderMessages,
  loadingOlderMessages,
  conversationName,
  contactAvatarUrl,
  titleCardRef
}: HeaderProps) => (
  <>
    {!hasMoreOlderMessages && (
      <div ref={titleCardRef} className={styles.titleCardWrapper}>
        <TitleCard
          title={conversationName}
          titleContainerClassName={styles.titleContainer}
          titleAvatarClassName={styles.titleAvatar}
          cardClassName={styles.inboxTitleCard}
          titleClassName={styles.inboxTitle}
          cardGradient="var(--app-page-bg)"
          cardColor="var(--ion-text-color)"
          avatarUrl={contactAvatarUrl}
          avatarInitials={getInitials(conversationName)}
        />
      </div>
    )}
    {loadingOlderMessages && (
      <div className={styles.loadingOlderMessages}>
        <IonSpinner name="bubbles" color="primary" />
      </div>
    )}
  </>
));
Header.displayName = 'Header';
// ============================================================

interface ConversationThreadProps {
  /** Conversation id (Wix conversation id, `sms-<digits>`, or `temp-<ts>`). Replaces useParams(). */
  id: string;
  /** Called when the user taps the back button. Replaces ionRouter.push(inboxRoute, 'back'). */
  onBack: () => void;
  /** Called when the user taps "View Contact". Replaces ionRouter.push('/tabs/contact/'+contactId). */
  onViewContact?: (contactId: string) => void;
  /** Seed values that used to arrive via router location.state from Inbox's list item click. */
  initialContactName?: string;
  initialAvatarUrl?: string;
  initialIsBlankShell?: boolean;
  initialRealConversationId?: string;
  /** Seed the composer channel on first mount (e.g. ContactsListModal's "Message" = 'sms',
   *  "Email" = 'email'). Applied once; the user can still switch afterward. */
  initialChannel?: 'chat' | 'email' | 'sms';
  /** Seed the outbound email account on first mount (e.g. ContactsListModal's "Email" = 'support'). */
  initialFromEmail?: 'scheduling' | 'billing' | 'support';
  /** The contact, when the caller already knows it — lets the stored emails load without waiting for a lookup */
  initialContactId?: string;
  /** Phone numbers (E.164) of the contact's texts, when the caller already knows them (the Inbox list does) */
  initialSmsPhones?: string[];
  /** Override the default call-button behavior (used when already embedded inside the dialer modal).
   *  Receives the number this conversation resolves to (SMS or contact phone) and the contact's name. */
  onCallClick?: (phone: string, contactName?: string) => void;
  /** Whether to render the outer <IonPage>. Set false when embedding inside an IonModal. Default true. */
  wrapInIonPage?: boolean;
}

/** sms-conversations docs are keyed by the E.164 phone number, e.g. +14258553424 */
const toE164 = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  if (!digits) return '';
  if (phone.trim().startsWith('+')) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  return `+${digits}`;
};

/** The company's logo — the avatar for texts sent from the company line ("RS Maids"). */
let companyIdentity: Promise<{ logoUrl?: string }> | null = null;
const getCompanyIdentity = () => {
  if (!companyIdentity) {
    companyIdentity = getWixBusinessProfile()
      .then(profile => ({ logoUrl: profile?.logoUrl }))
      .catch(() => {
        companyIdentity = null; // try again next time
        return {} as { logoUrl?: string };
      });
  }
  return companyIdentity;
};

/** A file uploaded to the site's Wix Media Manager for an email (uploadEmailAttachment answers with this) */
type UploadedAttachment = { id: string; url: string; name: string; mimeType: string; size: number; width?: number; height?: number };

/** A file chosen for the email being written: uploaded as soon as it is picked, so sending is instant */
type ComposerFile = {
  key: string;
  name: string;
  size: number;
  status: 'uploading' | 'ready' | 'error';
  error?: string;
  uploaded?: UploadedAttachment;
};

// What an email can carry — the email carrier and Wix both cap message size (the server enforces the same)
const MAX_EMAIL_FILES = 10;
const MAX_EMAIL_FILE_BYTES = 10 * 1024 * 1024;
const MAX_EMAIL_FILES_TOTAL_BYTES = 15 * 1024 * 1024;

const fileSizeLabel = (bytes: number): string =>
  bytes >= 1024 * 1024 ? i18n.t('components.ConversationThread.mb', { v1: (bytes / (1024 * 1024)).toFixed(1) }) : i18n.t('components.ConversationThread.kb', { v1: Math.max(1, Math.round(bytes / 1024)) });

/** An outgoing email as the app holds it until the saved copy comes back. Unlike a text it can't share the saved
 *  message's id (Wix chooses that), so the server stores this id on the saved message as `clientMessageId`. */
type PendingEmail = {
  id: string;
  subject: string;
  body: string;
  account: 'scheduling' | 'billing' | 'support';
  at: number;
  name?: string;
  avatar?: string;
  attachments: UploadedAttachment[];
  status: 'sending' | 'sent' | 'failed';
};

/** An outgoing text as the app holds it until the saved copy comes back: same id as the saved message */
type PendingSms = {
  id: string;
  body: string;
  at: number;
  target: string;
  name?: string;
  avatar?: string;
  staffId?: string;
  status: 'sending' | 'sent' | 'failed';
};

const ConversationThread: React.FC<ConversationThreadProps> = ({
  id: urlId,
  onBack,
  onViewContact,
  initialContactName,
  initialAvatarUrl,
  initialIsBlankShell,
  initialRealConversationId,
  initialChannel,
  initialFromEmail,
  initialContactId,
  initialSmsPhones,
  onCallClick,
  wrapInIonPage = true,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth(); // Get auth context to access user's wixContactId

  const [fromEmail, setFromEmail] = React.useState<'scheduling' | 'billing' | 'support'>(() => {
    if (initialFromEmail) return initialFromEmail;
    const saved = localStorage.getItem('lastUsedFromEmail');
    return (saved === 'scheduling' || saved === 'billing' || saved === 'support') ? saved : 'scheduling';
  });

  // Consolidated state management using extracted hooks
  const {
    message,
    setMessage,
    subject,
    setSubject,
    displayName,
    setDisplayName,
    contactAvatarUrl,
    setContactAvatarUrl,
    contactId,
    setContactId,
    realId,
    setRealId,
    isBlankShellActive,
    setIsBlankShellActive,
    channel,
    setChannel,
    showOptionsModal,
    setShowOptionsModal,
    selectedImage,
    setSelectedImage,
    showImageModal,
    setShowImageModal,
    showAddModal,
    setShowAddModal,
    showTemplatesModal,
    setShowTemplatesModal
  } = useConversationState(
    urlId,
    initialChannel,
    initialContactName,
    initialAvatarUrl ? (proxyImage(initialAvatarUrl) || getDefaultAvatar(initialContactName || 'Contact')) : undefined,
  );

  // Refs for scroll animation
  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);

  // Consolidated refs management using extracted hook
  const {
    titleCardRef,
    scrollToBottomTimeoutRef,
    hasScrolledToBottomRef,
  } = useConversationRefs();

  // Snapshot of values the "read once per id" effect below needs to check
  // without becoming reactive to every one of their changes — kept current
  // via a plain per-render effect (no dep array) rather than exhaustive-deps
  // on the effect itself, which would defeat its deliberate mount-per-id
  // behavior.
  const latestForInitialEffectRef = useRef({
    initialContactName, initialIsBlankShell, initialAvatarUrl, initialRealConversationId,
    displayName, isBlankShellActive, realId,
  });
  useEffect(() => {
    latestForInitialEffectRef.current = {
      initialContactName, initialIsBlankShell, initialAvatarUrl, initialRealConversationId,
      displayName, isBlankShellActive, realId,
    };
  });

  // Extract send message logic into custom hook
  const { handleSend: sendMessage } = useSendMessage();

  // Use the real ID if we found one, otherwise use the URL ID
  const id = realId || urlId;

  // SMS conversation detection
  const isSmsConversation = urlId?.startsWith('sms-') ?? false;
  const smsPhone = isSmsConversation ? `+${urlId.replace('sms-', '')}` : '';
  const isEmailChannel = ['email', 'gmail', 'outlook', 'yahoo', 'other-email'].includes(channel);

  // Contact phone number for non-SMS conversations
  const [contactPhone, setContactPhone] = useState<string>('');

  // Texts just sent: each gets an id of its own, shows in the thread at once, and is sent to the server under
  // that id. The saved message comes back with the same id, so the two are matched by id — nothing is compared
  // or hidden by text. A text that fails stays in the thread, marked, with a retry.
  const [pendingSms, setPendingSms] = useState<PendingSms[]>([]);
  const [pendingEmails, setPendingEmails] = useState<PendingEmail[]>([]);
  const [composerFiles, setComposerFiles] = useState<ComposerFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [company, setCompany] = useState<{ logoUrl?: string }>({});
  useEffect(() => {
    let cancelled = false;
    getCompanyIdentity().then((identity) => { if (!cancelled) setCompany(identity); });
    return () => { cancelled = true; };
  }, []);

  // The contact's texts, from the shared sms-conversations store (what the Inbox's SMS rows are built
  // from), so a Wix conversation reads as one conversation with the contact's chat, email and texts.
  // Not for an sms- thread: its own messages already load from that store (see FirestoreCacheService).
  // The numbers are known WITHOUT waiting on a lookup — the ones the Inbox list folded into this row, else the
  // contact's phone from the cached contact list — and only then the slower contact fetch (contactPhone). That
  // is what lets the chat and the texts load together instead of the texts trailing in.
  const cachedContactPhone = !isSmsConversation && (contactId || initialContactId)
    ? getCachedContactPhone((contactId || initialContactId) as string)
    : undefined;
  const smsPhones = useMemo(() => {
    if (isSmsConversation) return [];
    if (initialSmsPhones && initialSmsPhones.length > 0) return initialSmsPhones.map(toE164).filter(Boolean);
    const e164 = toE164(cachedContactPhone || contactPhone);
    return e164 ? [e164] : [];
  }, [isSmsConversation, initialSmsPhones, cachedContactPhone, contactPhone]);
  const { messages: smsThreadMessages, loading: smsLoading, mayHaveOlder: smsMayHaveOlder } = useSmsThreads(smsPhones);

  // Older texts (past the newest page above) come from Twilio's log as you scroll up
  const smsKey = smsPhones.join('|');
  const [prevSmsKey, setPrevSmsKey] = useState(smsKey);
  const [olderSms, setOlderSms] = useState<SmsMessage[]>([]);
  const [smsExhausted, setSmsExhausted] = useState(false);
  const [loadingOlderSms, setLoadingOlderSms] = useState(false);
  if (prevSmsKey !== smsKey) {
    setPrevSmsKey(smsKey);
    setOlderSms([]);
    setSmsExhausted(false);
  }
  const smsHasMoreOlder = smsMayHaveOlder && !smsExhausted;

  // Default channel to sms for SMS conversations — unless the caller explicitly
  // seeded a channel (e.g. ContactsListModal's "Email" on an sms- thread wants
  // the email composer, not sms).
  useEffect(() => {
    if (isSmsConversation && !initialChannel) setChannel('sms');
  }, [isSmsConversation, initialChannel, setChannel]);

  // Seed the composer channel from the caller (ContactsListModal's Message/Email
  // slider buttons). useConversationState's initializer already applies this on
  // the very first mount; this effect re-applies it when the SAME
  // ConversationThread instance is reused for a different conversation (urlId
  // changes without a remount — e.g. tapping Email then Message from the
  // SoftPhone contact picker). After that the user's own channel switches take
  // over for that conversation.
  const lastSeededForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialChannel) return;
    if (lastSeededForRef.current === urlId) return;
    lastSeededForRef.current = urlId;
    setChannel(initialChannel);
  }, [initialChannel, urlId, setChannel]);

  // State for messages
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMoreOlderMessages, setHasMoreOlderMessages] = useState(false);

  // Refs for managing pagination and message tracking
  const messagesRef = useRef<Message[]>([]);
  const seenMessageIdsRef = useRef<Set<string>>(new Set());
  const chatScrollerRef = useRef<{ scrollToBottom: (behavior?: 'auto' | 'smooth') => void; getScrollableRange: () => number }>(null);

  // Sync messages to ref
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Memoize callbacks to prevent unnecessary effect reruns
  const onMessagesLoaded = useCallback((loadedMessages: Message[]) => {
    console.log(`📭 [ConversationThread] Initial messages loaded, setting ${loadedMessages.length} messages`);
    setMessages(loadedMessages);
    loadedMessages.forEach(m => {
      if (m.id) seenMessageIdsRef.current.add(m.id);
    });
  }, []);

  const onLoadingStateChange = useCallback((loading: boolean) => {
    console.log(`📭 [ConversationThread] Loading state changed to ${loading}`);
    setLoading(loading);
  }, []);

  const onHasMoreOlderMessagesChange = useCallback((hasMore: boolean) => {
    console.log(`📭 [ConversationThread] hasMoreOlderMessages changed to ${hasMore}`);
    setHasMoreOlderMessages(hasMore);
  }, []);

  // HOOK 1: Message Loading - handles initial load from Firestore cache
  const { initialLoadDone } = useMessageLoading({
    realId: id,
    contactName: displayName,
    contactAvatarUrl,
    onMessagesLoaded,
    onLoadingStateChange,
    onHasMoreOlderMessagesChange,
    seenMessageIdsRef,
  });

  // HOOK 2: Message Subscription - handles real-time message updates
  useMessageSubscription({
    realId: id,
    initialLoadDone,
    contactName: displayName,
    contactAvatarUrl,
    setMessages,
    seenMessageIdsRef,
  });

  // The texts are waited for only until the thread has been shown once for this conversation. If their phone
  // number turns up late (the fallback lookup) they just arrive; the thread must not drop back to its
  // loading state and hide what is already on screen.
  const [shownFor, setShownFor] = useState<string | null>(null);
  const threadLoading = loading || (smsLoading && shownFor !== id);
  if (!threadLoading && shownFor !== id) setShownFor(id);

  // The spinner only appears if loading is actually slow. Opening an already-cached conversation takes a few
  // dozen milliseconds, and flashing a loader for that reads as "loading every time".
  const [spinnerDue, setSpinnerDue] = useState(false);
  useEffect(() => {
    if (!threadLoading) return;
    const timer = setTimeout(() => setSpinnerDue(true), 250);
    return () => {
      clearTimeout(timer);
      setSpinnerDue(false);
    };
  }, [threadLoading, id]);

  // Height of the on-screen keyboard, reported by the message bar; the list leaves room for it so the
  // last message stays visible above the bar when the keyboard opens.
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  // Timing: milliseconds from this thread opening to each milestone, so a slow open shows which
  // part (messages, contact lookup + emails) it is waiting on.
  const mountedAtRef = useRef(0);
  useEffect(() => {
    mountedAtRef.current = performance.now();
    console.log(`⏱️ [PERF] Thread: mounted ${msSinceOpen(mountedAtRef.current)}ms after the tap`);
  }, []);
  useEffect(() => {
    if (!loading) console.log(`⏱️ [PERF] Thread: messages loaded ${msSinceOpen(mountedAtRef.current)}ms after the tap`);
  }, [loading]);
  useEffect(() => {
    if (!smsLoading) console.log(`⏱️ [PERF] Thread: texts ready ${msSinceOpen(mountedAtRef.current)}ms after the tap`);
  }, [smsLoading]);
  useEffect(() => {
    if (!threadLoading) console.log(`⏱️ [PERF] Thread: shown ${msSinceOpen(mountedAtRef.current)}ms after the tap`);
  }, [threadLoading]);

  // Merge chat/SMS messages with stored emails, sorted chronologically
  const allMessages = useMemo(() => {
    // Texts from the shared SMS thread, minus any the thread already shows from the staff-specific
    // store (same text within a few seconds)
    const existingSms = messages.filter(m => m.channel === 'sms');
    const smsAsMessages = [...smsThreadMessages, ...olderSms]
      .filter(sm => !existingSms.some(m =>
        m.text === sm.body && Math.abs(new Date(m._createdDate).getTime() - sm.timestamp) < 10_000))
      .map(sm => ({
        id: `sms-thread-${sm.id}`,
        // An outgoing text's own 'from' is the shared Twilio number, not a person: the sender is only the
        // staff member recorded on it (sendSms looks them up from the phone agents)
        from: sm.from,
        text: sm.body,
        channel: 'sms' as const,
        _createdDate: new Date(sm.timestamp),
        isSystemMessage: false,
        isFromCustomer: sm.direction === 'inbound',
        ...(sm.direction === 'outbound' && {
          senderStaffName: sm.senderStaffName,
          senderStaffAvatar: sm.senderStaffAvatar,
          senderStaffId: sm.senderStaffId,
        }),
        _smsData: {
          direction: sm.direction,
          customerNumber: sm.direction === 'inbound' ? sm.from : sm.to,
        },
      })) as unknown as Message[];
    // Texts still on their way: shown until the saved message with the same id exists
    const savedIds = new Set<string>([
      ...messages.map(m => String(m.id ?? '')),
      ...smsThreadMessages.map(sm => sm.id),
    ]);
    const pendingAsMessages = pendingSms
      .filter(p => !savedIds.has(p.id))
      .map(p => ({
        id: p.id,
        from: p.name || '',
        text: p.body,
        channel: 'sms' as const,
        _createdDate: new Date(p.at),
        isSystemMessage: false,
        isFromCustomer: false,
        senderStaffName: p.name,
        senderStaffAvatar: p.avatar,
        deliveryStatus: p.status === 'failed' ? ('failed' as const) : undefined,
        _smsData: { direction: 'outbound' as const, customerNumber: p.target },
      })) as unknown as Message[];
    // Emails still on their way: shown until the saved message carrying the same clientMessageId exists
    const savedClientIds = new Set<string>(
      messages.map(m => (m as { clientMessageId?: string }).clientMessageId).filter((v): v is string => !!v)
    );
    const pendingEmailsAsMessages = pendingEmails
      .filter(p => !savedClientIds.has(p.id))
      .map(p => ({
        id: p.id,
        from: p.name || '',
        text: p.body,
        subject: p.subject,
        channel: 'email' as const,
        channelBadge: { text: t('components.ConversationThread.email', { v1: p.account.charAt(0).toUpperCase(), v2: p.account.slice(1) }) },
        _createdDate: new Date(p.at),
        isSystemMessage: false,
        isFromCustomer: false,
        senderStaffName: p.name,
        senderStaffAvatar: p.avatar,
        senderType: 'staff',
        clientMessageId: p.id,
        media: p.attachments.length > 0
          ? p.attachments.map(a => ({ url: a.url, type: /^image\/(png|jpe?g|gif|webp|bmp)$/i.test(a.mimeType) ? ('image' as const) : ('file' as const), name: a.name }))
          : undefined,
        deliveryStatus: p.status === 'failed' ? ('failed' as const) : undefined,
      })) as unknown as Message[];
    if (smsAsMessages.length === 0 && pendingAsMessages.length === 0 && pendingEmailsAsMessages.length === 0) return messages;
    return [...messages, ...smsAsMessages, ...pendingAsMessages, ...pendingEmailsAsMessages].sort(
      (a, b) => new Date(a._createdDate).getTime() - new Date(b._createdDate).getTime()
    );
  }, [messages, smsThreadMessages, olderSms, pendingSms, pendingEmails]);

  // Fetch contact phone for non-SMS conversations
  useEffect(() => {
    if (isSmsConversation || !contactId) return;
    let cancelled = false;
    const fetchPhone = async () => {
      try {
        const { ApiDataService } = await import('@rsmaids/shared/services/api/ApiDataService');
        const api = new ApiDataService();
        const contact = await api.getContactById(contactId);
        if (!cancelled && contact?.phones?.length) {
          const primary = contact.phones.find(p => p.primary) ?? contact.phones[0];
          const phone = primary.e164Phone || primary.phone;
          if (phone) setContactPhone(phone);
        }
      } catch (e) {
        console.error('[ConversationThread] Failed to fetch contact phone:', e);
      }
    };
    fetchPhone();
    return () => { cancelled = true; };
  }, [contactId, isSmsConversation]);

  // Fetch contactId from conversation document (enriched by Pub/Sub)
  useEffect(() => {
    if (!id || id.startsWith('temp-')) {
      setContactId(undefined);
      return;
    }

    let cancelled = false;
    const fetchContactId = async () => {
      try {
        const db = getFirestore(getApp(), 'wix-chat');
        const convRef = doc(db, 'conversations', id);
        const convSnap = await getDoc(convRef);

        if (!cancelled && convSnap.exists()) {
          const conversationData = convSnap.data();
          setContactId(conversationData?.participantContactId || conversationData?.contactId);
        } else if (!cancelled && !initialContactId) {
          setContactId(undefined); // never keep the previous conversation's contact
        }
      } catch (error) {
        console.error('Failed to fetch contactId from conversation:', error);
      }
    };

    fetchContactId();
    return () => { cancelled = true; };
  }, [id, setContactId, initialContactId]);

  // HOOK 3: Message Pagination - handles loading older messages when scrolling
  const { loadingOlderMessages, handleLoadOlderMessages } = useConversationMessages({
    id,
    contactName: displayName,
    contactAvatarUrl,
    setMessages,
    hasMoreOlderMessages,
    setHasMoreOlderMessages,
    seenMessageIdsRef,
    initialLoadDone,
    messagesRef,
  });

  // Older texts: the Twilio log, a page per number, before the oldest text loaded for that number
  const loadOlderSms = useCallback(async () => {
    if (loadingOlderSms || !smsHasMoreOlder || smsPhones.length === 0) return;
    setLoadingOlderSms(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      const token = await currentUser.getIdToken();
      const base = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || 'https://us-central1-rsmaidsbooking.cloudfunctions.net';
      const loaded = [...smsThreadMessages, ...olderSms];
      const pages = await Promise.all(smsPhones.map(async (phone) => {
        const times = loaded.filter(m => m.from === phone || m.to === phone).map(m => m.timestamp).filter(Boolean);
        if (times.length === 0) return [] as SmsMessage[];
        const before = new Date(Math.min(...times)).toISOString();
        const response = await fetch(
          `${base}/getOlderSmsMessages?phone=${encodeURIComponent(phone)}&before=${encodeURIComponent(before)}&limit=${SMS_THREAD_PAGE}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error(`getOlderSmsMessages failed: HTTP ${response.status}`);
        return ((await response.json()) as { messages: SmsMessage[] }).messages;
      }));
      setOlderSms(prev => {
        const have = new Set(prev.map(m => m.id));
        return [...prev, ...pages.flat().filter(m => !have.has(m.id))];
      });
      if (pages.every(page => page.length < SMS_THREAD_PAGE)) setSmsExhausted(true);
    } catch (err) {
      console.error('[ConversationThread] Failed to load older texts:', err);
    } finally {
      setLoadingOlderSms(false);
    }
  }, [loadingOlderSms, smsHasMoreOlder, smsPhones, smsThreadMessages, olderSms]);

  const hasMoreOlder = hasMoreOlderMessages || smsHasMoreOlder;
  const loadingOlder = loadingOlderMessages || loadingOlderSms;
  const loadOlderAll = useCallback(async () => {
    await Promise.all([handleLoadOlderMessages(), loadOlderSms()]);
  }, [handleLoadOlderMessages, loadOlderSms]);

  // HOOK 4: Unified Items (Calls/SMS) - fetch and merge with Wix messages
  const { unifiedItems } = useConversationUnified({
    conversationId: id,
    contactId: contactId || '', // Required parameter - will be populated from conversation doc
    enabled: !!id && !id.startsWith('temp-') && !!contactId, // Only fetch if we have both IDs
  });

  // Merge unified items with Wix messages — computed during render
  // (React's "adjust state when a dependency changes" pattern) rather than
  // as a setState call inside an effect, since it's a pure derivation from
  // already-available state with no external system involved.
  const [prevUnifiedItems, setPrevUnifiedItems] = useState({ calls: unifiedItems.calls, sms: unifiedItems.sms });
  if (prevUnifiedItems.calls !== unifiedItems.calls || prevUnifiedItems.sms !== unifiedItems.sms) {
    setPrevUnifiedItems({ calls: unifiedItems.calls, sms: unifiedItems.sms });
    if (unifiedItems.calls.length > 0 || unifiedItems.sms.length > 0) {
      setMessages((prevMessages) => {
        const merged = mergeConversationItems(
          prevMessages,
          unifiedItems.calls,
          unifiedItems.sms
        );
        // Convert merged items back to Message format for rendering
        return merged.map((item) => {
          if (item.type === 'wix') {
            // This is already a Wix message
            return item as unknown as Message;
          } else if (item.type === 'call') {
            // Convert call to a pseudo-message for rendering
            const callMessage: Message = {
              id: item.id,
              from: 'System',
              text: `${item.direction === 'inbound' ? 'Incoming' : 'Outgoing'} call - ${item.duration}s`,
              channel: 'call',
              _createdDate: item.timestamp,
              isSystemMessage: false,
              _callData: {
                callId: item.callId || '',
                direction: item.direction || 'inbound',
                duration: item.duration || 0,
                recordingUrl: item.recordingUrl,
                callerName: item.callerName,
                customerNumber: item.customerNumber || '',
              },
            };
            return callMessage;
          } else if (item.type === 'sms') {
            // Convert SMS to a pseudo-message for rendering
            const smsMessage: Message = {
              id: item.id,
              from: item.direction === 'inbound' ? item.customerNumber || 'Unknown' : 'You',
              text: item.messageText || '',
              channel: 'sms',
              _createdDate: item.timestamp,
              isSystemMessage: false,
              isFromCustomer: item.direction === 'inbound',
              _smsData: {
                direction: item.direction || 'inbound',
                customerNumber: item.customerNumber || '',
              },
            };
            return smsMessage;
          }
          return item as unknown as Message;
        });
      });
    }
  }

  // Note: applyStaffLookup is now imported from messageEnrichment service as applyStaffLookupService

  // Get contact name from initial props (passed by the caller — Inbox's list item click,
  // or SoftPhoneModal's selected-thread state)
  useEffect(() => {
    const {
      initialContactName, initialIsBlankShell, initialAvatarUrl, initialRealConversationId,
      displayName, isBlankShellActive, realId,
    } = latestForInitialEffectRef.current;
    const state = {
      contactName: initialContactName,
      isBlankShell: initialIsBlankShell,
      avatarUrl: initialAvatarUrl,
      realConversationId: initialRealConversationId,
    };
    console.log(`📭 ConversationThread - initial state:`, state);

    // If the caller already found the real conversation ID, use it immediately
    if (state.realConversationId && realId !== state.realConversationId) {
      console.log(`📭 Got real conversation ID from initial props: ${state.realConversationId}`);
      setRealId(state.realConversationId);
      // Skip blank shell - we already have the real ID from the caller
      setIsBlankShellActive(false);
      console.log(`📭 Set blank shell active: false (got real ID from props)`);
    } else if (!state.realConversationId) {
      // Track if we're in blank shell mode - only true on initial navigation with temp ID
      if (state.isBlankShell && !isBlankShellActive) {
        setIsBlankShellActive(true);
        console.log(`📭 Set blank shell active: true`);
      } else if (!state.isBlankShell && isBlankShellActive) {
        setIsBlankShellActive(false);
      }
    }

    // If initial props have contact name, update only if different
    if (state.contactName && displayName !== state.contactName) {
      setDisplayName(state.contactName);
      // Pre-cache under current ID
      localStorage.setItem(`conversation-${id}-contactName`, state.contactName);
      console.log(`📭 Set contact name from props: "${state.contactName}"`);
      return;
    }

    // Try to get from localStorage as fallback (when returning to a conversation)
    if (!state.contactName) {
      const cached = localStorage.getItem(`conversation-${id}-contactName`);
      if (cached && displayName === '') {
        // Only load from cache if we haven't already set a contact name
        setDisplayName(cached);
        console.log(`📭 Set contact name from cache: "${cached}"`);
      }
    }
    // Matches original: read once per id, not reactive to every prop change.
    // Non-id inputs are read from latestForInitialEffectRef (kept fresh by a
    // separate per-render effect above) instead of being listed here, since
    // adding them as deps would make this effect reactive.
  }, [id, setRealId, setIsBlankShellActive, setDisplayName]);

  // Listen for real conversation ID updates via window event
  useEffect(() => {
    const handleRealConversationFound = (event: CustomEvent) => {
      const { conversationId, contactName } = event.detail;
      if (conversationId && !conversationId.startsWith('temp-')) {
        console.log(`📭 Received real conversation ID via event: ${conversationId}`);
        setRealId(conversationId);
        setIsBlankShellActive(false);
        // Also update contact name if provided
        if (contactName) {
          setDisplayName(contactName);
          localStorage.setItem(`conversation-${conversationId}-contactName`, contactName);
        }
      }
    };

    window.addEventListener('realConversationFound', handleRealConversationFound as EventListener);
    return () => window.removeEventListener('realConversationFound', handleRealConversationFound as EventListener);
  }, [setRealId, setIsBlankShellActive, setDisplayName]);



  // When we transition from temp to real ID, migrate localStorage entries
  useEffect(() => {
    if (urlId?.startsWith('temp-') && realId && !realId.startsWith('temp-')) {
      console.log(`📭 Migrating localStorage from temp ID ${urlId} to real ID ${realId}`);
      // Get the contact name from the temp ID cache
      const tempContactName = localStorage.getItem(`conversation-${urlId}-contactName`);
      if (tempContactName) {
        // Store it under the real ID
        localStorage.setItem(`conversation-${realId}-contactName`, tempContactName);
        console.log(`📭 Migrated contact name to real ID: ${tempContactName}`);
      }
      // Can optionally clean up temp ID cache
      localStorage.removeItem(`conversation-${urlId}-contactName`);

      // Deactivate blank shell mode once we have the real ID
      setIsBlankShellActive(false);
      console.log(`📭 Deactivated blank shell mode`);
    }
  }, [urlId, realId, setIsBlankShellActive]);

  const conversationName = displayName || ''; // Show contact name when loaded (empty until then)

  // Handle avatar URL from initial props
  useEffect(() => {
    if (initialAvatarUrl) {
      const proxiedUrl = proxyImage(initialAvatarUrl) || getDefaultAvatar(displayName || 'Contact');
      setContactAvatarUrl(proxiedUrl);
      console.log(`📸 Got avatar URL from initial props:`, initialAvatarUrl, `-> proxied:`, proxiedUrl);
    }
  }, [initialAvatarUrl, setContactAvatarUrl, displayName]);

  // Reset scroll flag and cleanup timeout when conversation changes
  useEffect(() => {
    hasScrolledToBottomRef.current = false;
    // Clear any pending scroll timeout
    if (scrollToBottomTimeoutRef.current) {
      clearTimeout(scrollToBottomTimeoutRef.current);
      scrollToBottomTimeoutRef.current = null;
    }
    return () => {
      // Cleanup on unmount
      if (scrollToBottomTimeoutRef.current) {
        clearTimeout(scrollToBottomTimeoutRef.current);
      }
    };
  }, [id, hasScrolledToBottomRef, scrollToBottomTimeoutRef]);



  // Send one text to Twilio (via sendSms) under its own id. Safe to call again for the same text: the server
  // sees the id and doesn't text twice.
  const sendPendingSms = useCallback(async (entry: PendingSms) => {
    setPendingSms(prev => prev.map(p => (p.id === entry.id ? { ...p, status: 'sending' } : p)));
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Not signed in');
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || 'https://us-central1-rsmaidsbooking.cloudfunctions.net'}/sendSms`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: entry.target,
            body: entry.body,
            clientMessageId: entry.id,
            contactName: displayName || undefined,
            staffName: entry.name || undefined,
            staffAvatar: entry.avatar || undefined,
            staffId: entry.staffId || undefined,
          }),
        }
      );
      if (!response.ok) throw new Error(`sendSms failed: HTTP ${response.status}`);
      // Accepted. It stays on screen until the saved copy arrives; this just tidies up if that never does.
      setPendingSms(prev => prev.map(p => (p.id === entry.id ? { ...p, status: 'sent' } : p)));
      window.setTimeout(() => setPendingSms(prev => prev.filter(p => p.id !== entry.id)), 60_000);
    } catch (err) {
      console.error('[ConversationThread] SMS send error:', err);
      setPendingSms(prev => prev.map(p => (p.id === entry.id ? { ...p, status: 'failed' } : p)));
    }
  }, [displayName]);

  // ── Files for the email being written ─────────────────────────────────────────────────────────
  // Each file is uploaded to the site's Wix Media Manager as soon as it is picked; the email is then sent with
  // references to the uploaded files (the server attaches them to the email and to its Wix Inbox message).
  const uploadComposerFile = useCallback(async (key: string, file: File) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Not signed in');
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || 'https://us-central1-rsmaidsbooking.cloudfunctions.net'}/uploadEmailAttachment`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            // Always octet-stream, so the server receives the bytes untouched; the real type travels separately
            'Content-Type': 'application/octet-stream',
            'X-File-Name': encodeURIComponent(file.name),
            'X-Mime-Type': file.type || 'application/octet-stream',
          },
          body: file,
        },
      );
      if (!response.ok) throw new Error(`upload failed: HTTP ${response.status}`);
      const uploaded = await response.json() as UploadedAttachment;
      setComposerFiles(prev => prev.map(f => (f.key === key ? { ...f, status: 'ready', uploaded } : f)));
    } catch (err) {
      console.error('[ConversationThread] attachment upload error:', err);
      setComposerFiles(prev => prev.map(f => (f.key === key ? { ...f, status: 'error', error: t('components.ConversationThread.attachmentUploadFailed') } : f)));
    }
  }, [t]);

  const handleFilesPicked = useCallback((picked: FileList | null) => {
    if (!picked || picked.length === 0) return;
    let count = composerFiles.length;
    let total = composerFiles.reduce((sum, f) => sum + f.size, 0);
    const added: ComposerFile[] = [];
    const toUpload: Array<{ key: string; file: File }> = [];
    Array.from(picked).forEach((file) => {
      const key = newId();
      let error: string | undefined;
      if (count >= MAX_EMAIL_FILES) error = t('components.ConversationThread.tooManyFiles', { max: MAX_EMAIL_FILES });
      else if (file.size > MAX_EMAIL_FILE_BYTES) error = t('components.ConversationThread.fileTooLarge', { max: '10 MB' });
      else if (total + file.size > MAX_EMAIL_FILES_TOTAL_BYTES) error = t('components.ConversationThread.attachmentsTooLarge', { max: '15 MB' });
      if (error) {
        added.push({ key, name: file.name, size: file.size, status: 'error', error });
        return;
      }
      count += 1;
      total += file.size;
      added.push({ key, name: file.name, size: file.size, status: 'uploading' });
      toUpload.push({ key, file });
    });
    setComposerFiles(prev => [...prev, ...added]);
    toUpload.forEach(({ key, file }) => { void uploadComposerFile(key, file); });
  }, [composerFiles, uploadComposerFile, t]);

  const footerAttachments: FooterAttachment[] = useMemo(
    () => composerFiles.map(f => ({ key: f.key, name: f.name, sizeLabel: fileSizeLabel(f.size), status: f.status, error: f.error })),
    [composerFiles],
  );

  // Send one email: the thread already shows it (pending), and the server saves it under the same clientMessageId so the
  // saved message replaces the pending one. Safe to call again for the same email: the server won't send it twice.
  const sendPendingEmail = useCallback(async (entry: PendingEmail) => {
    setPendingEmails(prev => prev.map(p => (p.id === entry.id ? { ...p, status: 'sending' } : p)));
    const markFailed = () => setPendingEmails(prev => prev.map(p => (p.id === entry.id ? { ...p, status: 'failed' } : p)));
    await sendMessage({
      id,
      message: entry.body,
      channel,
      user: user || undefined,
      displayName,
      contactName: displayName,
      contactAvatarUrl,
      subject: entry.subject,
      fromEmail: entry.account,
      clientMessageId: entry.id,
      attachments: entry.attachments,
      onMessageSent: () => {
        // Accepted. It stays on screen until the saved message arrives; this just tidies up if that never does.
        setPendingEmails(prev => prev.map(p => (p.id === entry.id ? { ...p, status: 'sent' } : p)));
        window.setTimeout(() => setPendingEmails(prev => prev.filter(p => p.id !== entry.id)), 60_000);
      },
      onClearInput: () => { /* the composer was cleared when the email was queued */ },
      onError: markFailed,
    });
  }, [id, channel, user, displayName, contactAvatarUrl, sendMessage]);

  const retryEmail = useCallback((emailId: string) => {
    const entry = pendingEmails.find(p => p.id === emailId);
    if (entry) void sendPendingEmail(entry);
  }, [pendingEmails, sendPendingEmail]);

  const retrySms = useCallback((id: string) => {
    const entry = pendingSms.find(p => p.id === id);
    if (entry) void sendPendingSms(entry);
  }, [pendingSms, sendPendingSms]);

  // Use the handleSend from useSendMessage hook directly
  // The hook manages optimistic message updates and input clearing internally
  const handleSend = useCallback(async () => {
    // Texts always go out through Twilio — Wix's SMS number is being retired, so Wix SMS is history to
    // read, never a channel to send on. The destination is this thread's number, or on a Wix conversation
    // the contact's number. Nothing is sent (and the draft is kept) if that number isn't known.
    if (isSmsConversation || channel === 'sms') {
      const body = message.trim();
      const smsTarget = isSmsConversation ? smsPhone : (smsPhones[0] || '');
      if (!body) return;
      if (!smsTarget) {
        console.warn('[ConversationThread] No phone number known for this contact — SMS not sent');
        return;
      }
      setMessage('');
      // Who is sending, recorded on the text so the thread can show them
      // The sending staff member is found the way the app's other sends find them (useSendMessage): the staff
      // entry whose contactId is the signed-in user's wixContactId, which gives their staffMemberId, name and
      // avatar. With it the text shows the person's own avatar from the first frame, not the company logo until the
      // saved copy arrives.
      const staff = user?.wixContactId
        ? StaffCacheService.getInstance().getAllStaff().find(s => s.contactId === user.wixContactId)
        : undefined;
      const entry: PendingSms = {
        id: newId(),
        body,
        at: Date.now(),
        target: smsTarget,
        name: staff?.name,
        avatar: staff?.mainMedia || staff?.contactAvatar || undefined,
        staffId: staff?.staffMemberId,
        status: 'sending',
      };
      setPendingSms(prev => [...prev, entry]);
      void sendPendingSms(entry);
      return;
    }

    // An email is shown at once as a pending message, and sent in the background (see sendPendingEmail)
    if (isEmailChannel) {
      const body = message.trim();
      const attachments = composerFiles.flatMap(f => (f.status === 'ready' && f.uploaded ? [f.uploaded] : []));
      if (!body && attachments.length === 0) return;
      if (composerFiles.some(f => f.status === 'uploading')) return; // wait for the files to finish uploading
      const staff = user?.wixContactId
        ? StaffCacheService.getInstance().getAllStaff().find(s => s.contactId === user.wixContactId)
        : undefined;
      const entry: PendingEmail = {
        id: newId(),
        subject: subject.trim() || 'R&S Maids Service sent you a message', // i18n-ignore: sent to the customer exactly as written (the server's default subject)
        body,
        account: fromEmail,
        at: Date.now(),
        name: staff?.name || user?.name,
        avatar: staff?.mainMedia || staff?.contactAvatar || user?.avatarUrl || undefined,
        attachments,
        status: 'sending',
      };
      setPendingEmails(prev => [...prev, entry]);
      setMessage('');
      setComposerFiles([]);
      void sendPendingEmail(entry);
      return;
    }

    // Get targetChannelId from the latest message for Wix channel routing
    // targetChannelIds is an array - use the first one for replies
    const latestMessage = messages.length > 0 ? messages[messages.length - 1] : null;
    const targetChannelIds = latestMessage?.targetChannelIds;
    const targetChannelId = targetChannelIds && targetChannelIds.length > 0 ? targetChannelIds[0] : undefined;

    sendMessage({
      id,
      message,
      channel,
      user: user || undefined,
      displayName,
      contactName: displayName,
      contactAvatarUrl,
      targetChannelId, // Use targetChannelId for outbound routing
      subject,
      fromEmail: isEmailChannel ? fromEmail : undefined,
      onMessageSent: () => {
        // onMessageSent callback is already handled by hook
      },
      onClearInput: () => {
        setMessage('');
      }
    });
  }, [isSmsConversation, smsPhone, smsPhones, message, id, channel, user, displayName, contactAvatarUrl, messages, sendMessage, sendPendingSms, sendPendingEmail, setMessage, subject, fromEmail, isEmailChannel, composerFiles]);



  // Stable image click handler - memoized to prevent re-renders
  // Tapping an image shows it full screen inside the thread (see viewingImage): the same header, the messages kept
  // underneath, the composer hidden — not a separate modal.
  const handleImageClick = useCallback((attachment: { url?: string; type?: 'image' | 'video' | 'file' | 'audio' | 'icon'; name?: string; iconName?: string; duration?: number }) => {
    if (attachment.type === 'image' && attachment.url) {
      setSelectedImage({
        url: attachment.url,
        name: attachment.name,
        type: attachment.type
      });
      setShowImageModal(true);
    }
  }, [setSelectedImage, setShowImageModal]);
  const viewingImage = showImageModal && !!selectedImage?.url;
  const closeImage = useCallback(() => setShowImageModal(false), [setShowImageModal]);
  const [loadedImageUrl, setLoadedImageUrl] = useState<string | undefined>();

  // Android's back button closes the image first, before it would leave the conversation
  useEffect(() => {
    if (!viewingImage) return;
    const onBack = (event: Event) => {
      (event as CustomEvent<{ register: (priority: number, handler: () => void) => void }>).detail.register(100, closeImage);
    };
    document.addEventListener('ionBackButton', onBack);
    return () => document.removeEventListener('ionBackButton', onBack);
  }, [viewingImage, closeImage]);

  const [presentToast] = useIonToast();
  const imageFileName = useCallback(() => {
    const raw = (selectedImage?.name || 'photo').replace(/[^a-zA-Z0-9._-]+/g, '_'); // no spaces: Android cannot read the type (".jpeg") from an encoded name
    return /[.][a-zA-Z0-9]{2,5}$/.test(raw) ? raw : raw + '.jpg';
  }, [selectedImage]);

  // On the phone the file is fetched natively into the app's folder (no browser); on the web the browser opens it.
  const downloadImage = useCallback(async () => {
    if (!selectedImage?.url) return;
    if (!Capacitor.isNativePlatform()) {
      const link = document.createElement('a');
      link.href = selectedImage.url;
      link.download = selectedImage.name || '';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
      return;
    }
    try {
      // the plugin does not always create the folder itself (ENOENT), so make it first
      try { await Filesystem.mkdir({ path: 'R&S Maids', directory: Directory.Documents, recursive: true }); } catch { /* already there */ }
      await Filesystem.downloadFile({ url: selectedImage.url, path: 'R&S Maids/' + imageFileName(), directory: Directory.Documents, recursive: true });
      void presentToast({ message: t('components.ConversationThread.photoSaved'), duration: 2000, position: 'bottom', cssClass: 'app-toast', icon: checkmarkCircle });
    } catch (err) {
      console.error('Saving the photo failed:', err);
      void presentToast({ message: t('components.ConversationThread.photoFailed'), duration: 4000, position: 'bottom', cssClass: 'app-toast app-toast--error', icon: alertCircleOutline });
    }
  }, [selectedImage, imageFileName, presentToast, t]);

  // The real image file goes to the phone's share sheet (WhatsApp-style); the web falls back to sharing the link
  const shareImage = useCallback(async () => {
    if (!selectedImage?.url) return;
    try {
      if (Capacitor.isNativePlatform()) {
        try { await Filesystem.mkdir({ path: 'share', directory: Directory.Cache, recursive: true }); } catch { /* already there */ }
        const sharePath = 'share/' + imageFileName();
        await Filesystem.downloadFile({ url: selectedImage.url, path: sharePath, directory: Directory.Cache, recursive: true });
        // Share accepts only file:// addresses; the download result is a bare path
        const { uri } = await Filesystem.getUri({ path: sharePath, directory: Directory.Cache });
        await Share.share({ title: selectedImage.name, files: [uri] });
        return;
      }
      const { value: canShare } = await Share.canShare();
      if (canShare) await Share.share({ title: selectedImage.name, url: selectedImage.url });
    } catch (err) {
      // Dismissing the share sheet rejects on some platforms; anything else is shown
      if (!/cancel|dismiss/i.test(String((err as Error)?.message ?? err))) {
        console.error('Sharing the photo failed:', err);
        void presentToast({ message: t('components.ConversationThread.shareFailed'), duration: 4000, position: 'bottom', cssClass: 'app-toast app-toast--error', icon: alertCircleOutline });
      }
    }
  }, [selectedImage, imageFileName, presentToast, t]);

  // Simplified renderItem callback - delegates to MessageItemRender component
  // Signature: (message, index, previousMsg) - previousMsg now comes from ChatScroller
  const renderItem = useCallback((msg: Message, index: number, previousMsg?: Message) => {
    // Handle call messages
    if (msg.channel === 'call' && msg._callData) {
      return (
        <CallMessageItem
          key={msg.id || `call-${index}`}
          _callId={msg._callData.callId}
          direction={msg._callData.direction}
          duration={msg._callData.duration}
          callerName={msg._callData.callerName}
          customerNumber={msg._callData.customerNumber}
          recordingUrl={msg._callData.recordingUrl}
          timestamp={msg._createdDate}
          onPlayRecording={(url: string) => {
            // Open recording in modal or new tab
            window.open(url, '_blank');
          }}
        />
      );
    }

    // Texts use the same bubble as chat/Wix messages (MessageItemRender, which labels the channel as
    // SMS) so the thread looks one way — only the sender's name and avatar need filling in: the
    // contact's for incoming texts, since the SMS store carries just a phone number.
    let shown = msg;
    if (msg.channel === 'sms' && msg._smsData && msg.isFromCustomer) {
      shown = { ...msg, from: displayName || msg.from, avatarUrl: msg.avatarUrl || contactAvatarUrl || undefined };
    } else if (msg.channel === 'sms' && msg.isFromCustomer === false) {
      if (msg.senderStaffName) {
        // Outgoing text with a recorded sender (the phone agent's name): their avatar from the staff cache
        if (!msg.senderStaffAvatar) {
          const staff = StaffCacheService.getInstance().getStaffByName(msg.senderStaffName);
          const avatar = staff?.mainMedia || staff?.contactAvatar;
          if (avatar) shown = { ...msg, senderStaffAvatar: avatar };
        }
      } else {
        // No sender recorded. Every text in this store goes out through sendSms, which always sends from the
        // company's Twilio number — so this is the company line: "RS Maids" with the company logo. (Not a
        // person guessed from the number: a business number can be shared, so it doesn't say who typed it.)
        // i18n-ignore: the company's brand name, not translated
        shown = { ...msg, from: 'RS Maids', senderStaffName: 'RS Maids', senderStaffAvatar: company.logoUrl };
      }
    }

    return (
      <MessageItemRender
        key={msg.id || index}
        message={shown}
        previousMessage={previousMsg}
        onImageClick={handleImageClick}
        onRetry={msg.deliveryStatus === 'failed' ? () => (msg.channel === 'email' ? retryEmail(String(msg.id)) : retrySms(String(msg.id))) : undefined}
      />
    );
  }, [handleImageClick, displayName, contactAvatarUrl, company, retrySms, retryEmail]);

  // Handle scroll for header animation
  const handleScroll = useCallback((scrollTop: number, scrollHeight: number, clientHeight?: number) => {
    const collapseDistance = 66;
    // How far the list can scroll at all. A short conversation can scroll only a few pixels (the
    // jump to the bottom on open moves it that far), which used to leave the header half-way through
    // its fade — small title partly showing while the title card was still on screen. If it can't
    // scroll the whole collapse distance there is nothing to collapse: title card only.
    const scrollableRange = clientHeight !== undefined ? scrollHeight - clientHeight : Number.POSITIVE_INFINITY;
    // If no title card (loading older messages or has more to load), always show header title
    if (hasMoreOlder || loadingOlder) {
      baseHeaderRef.current?.style.setProperty('--title-opacity', '1');
    } else {
      // Animate header title based on scroll position
      const progress = scrollableRange < collapseDistance ? 0 : Math.min(scrollTop / collapseDistance, 1);
      console.log(`🪧 [header] scroll → small title ${progress.toFixed(2)} (scrollTop ${Math.round(scrollTop)}, scrollable range ${Math.round(scrollableRange)})`);
      baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
      if (titleCardAnimationRef.current) {
        titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
        titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
      }
    }
  }, [hasMoreOlder, loadingOlder]);

  // Opening a conversation reads it. Texts have a per-staff read marker (markSmsRead); a chat conversation's unread
  // flag and count live on its wix-chat document, which nothing ever cleared — so the Inbox kept its badge forever.
  // Runs again as messages arrive while the thread is open, since it is being looked at.
  const messageCountForRead = allMessages.length;
  useEffect(() => {
    if (isSmsConversation) {
      if (smsPhone) void markSmsRead(smsPhone);
      return;
    }
    smsPhones.forEach((phone) => { void markSmsRead(phone); });
    const db = getFirestore(getApp(), 'wix-chat');
    const myUid = auth.currentUser?.uid;
    if (id && myUid) {
      // only THIS staff member's own unread count: others keep theirs until they open it themselves
      updateDoc(doc(db, 'conversations', String(id)), { [`unreadBy.${myUid}`]: 0 })
        .catch(() => { /* no conversation document for this id (e.g. a contact who only has texts) — nothing to clear */ });
    }
  }, [id, isSmsConversation, smsPhone, smsPhones, messageCountForRead]);

  // Call button: opens the platform's soft phone (SoftPhoneModal on mobile, IvrPanel on
  // desktop) instead of the retired standalone DialerModal. Callers that already embed this
  // component inside the soft phone itself (SoftPhoneModal) override via onCallClick to avoid
  // reopening/circular-dispatching to the modal they're already inside.
  const handleCallClick = useCallback(() => {
    const phone = smsPhone || contactPhone;
    if (onCallClick) { onCallClick(phone || '', displayName || undefined); return; }
    if (import.meta.env.VITE_PLATFORM === 'desktop') {
      openIvrDialer(phone || undefined);
    } else {
      window.dispatchEvent(new CustomEvent('rs:openDialer', {
        detail: { phone, intent: 'keypad', contactName: displayName || undefined },
      }));
    }
  }, [onCallClick, smsPhone, contactPhone, displayName]);

  // The header bar's title (contact name + avatar) normally fades in only as the big title card
  // scrolls away. While there is no title card — the messages are still loading, or there are
  // none yet, or older ones are still being paged in — keep the header title visible, so the
  // header (name, avatar, buttons) is complete from the first frame instead of looking empty.
  const hasTitleCard = !threadLoading && allMessages.length > 0 && !hasMoreOlder && !loadingOlder;

  // Whether this conversation fits on one screen (nothing for the title card to collapse into the
  // header), remembered from the last time it was opened. While the messages load we don't know yet,
  // so we go by that: a short conversation keeps the small header title hidden from the start —
  // otherwise it shows for the length of the load and then disappears — and a long one (or one never
  // opened before) shows it, as the title card isn't there yet.
  const shortConversationKey = `conversation-${id}-fitsOnScreen`;
  const wasShortLastTime = (() => {
    try { return localStorage.getItem(shortConversationKey) === '1'; } catch { return false; }
  })();
  // Layout effect, so the header is right in the very first painted frame of the messages: it runs
  // after the list has been laid out (and pinned to the bottom) but before the browser paints. With
  // a title card, measure whether the conversation really needs scrolling: if it fits on screen the
  // title card is all you see (small title hidden); if it scrolls, the card is off the top and the
  // small title shows. Doing this in a normal effect painted one frame with the wrong title.
  useLayoutEffect(() => {
    let showSmallTitle: boolean;
    let reason: string;
    let range: number | undefined;
    if (threadLoading) {
      // A known-short conversation shows its title card while loading (see the loading branch below),
      // so the small title stays hidden; otherwise it shows until the messages are in.
      const cardShownWhileLoading = wasShortLastTime && !!conversationName;
      showSmallTitle = !cardShownWhileLoading;
      reason = cardShownWhileLoading ? 'loading, known-short: title card shown' : 'loading: no title card yet';
    } else if (!hasTitleCard) {
      showSmallTitle = true;
      reason = `loaded, no title card (hasMore=${hasMoreOlder}, loadingOlder=${loadingOlder}, messages=${allMessages.length})`;
    } else {
      range = chatScrollerRef.current?.getScrollableRange();
      showSmallTitle = range === undefined ? false : range >= 66;
      reason = `loaded, title card: scrollable range ${range}`;
    }
    console.log(`🪧 [header] small title ${showSmallTitle ? 'SHOWN' : 'hidden'} — ${reason}`);
    baseHeaderRef.current?.style.setProperty('--title-opacity', showSmallTitle ? '1' : '0');
  }, [hasTitleCard, threadLoading, wasShortLastTime, allMessages.length, conversationName, hasMoreOlder, loadingOlder]);

  // Once the messages are on screen, measure and remember whether the whole conversation fits
  useEffect(() => {
    if (threadLoading || allMessages.length === 0) return;
    const frame = requestAnimationFrame(() => {
      const range = chatScrollerRef.current?.getScrollableRange();
      if (range === undefined) return;
      try { localStorage.setItem(shortConversationKey, range < 66 ? '1' : '0'); } catch { /* ignore */ }
    });
    return () => cancelAnimationFrame(frame);
  }, [threadLoading, allMessages.length, shortConversationKey]);

  // Track newest message for ChatScroller auto-scroll
  // Tooltip handlers
  const Page = wrapInIonPage ? IonPage : React.Fragment;
  const pageProps = wrapInIonPage ? { className: styles.page } : {};
  // The ellipsis menu while an image is open: just what an image needs
  const imageMenuOptions = [
    {
      label: t('components.ConversationThread.downloadPhoto'),
      icon: downloadOutline,
      iconColor: 'primary',
      onClick: () => { setShowOptionsModal(false); void downloadImage(); },
    },
    {
      label: t('components.ConversationThread.sharePhoto'),
      icon: shareOutline,
      iconColor: 'primary',
      onClick: () => { setShowOptionsModal(false); void shareImage(); },
    },
    {
      label: t('components.ConversationThread.closePhoto'),
      icon: closeOutline,
      iconColor: 'primary',
      onClick: () => { setShowOptionsModal(false); closeImage(); },
    },
  ];

  return (
    <Page {...pageProps}>
      <BasePageWithHeader
        ref={baseHeaderRef}
        title={conversationName}
        toolbarClassName={styles.toolbar}
        smallTitleClassName={styles.smallTitle}
        titleHeaderClassName={styles.titleHeader}
        headerAvatarClassName={styles.headerAvatar}
        avatarUrl={contactAvatarUrl}
        avatarInitials={getInitials(conversationName)}
        startButtons={
          <>
            <IonButton
              shape="round"
              className={styles.backButton}
              onClick={viewingImage ? closeImage : onBack}
            >
              <IonIcon icon={chevronBackOutline} />
            </IonButton>
            <IonButton
              shape="round"
              className={styles.hiddenBalanceButton}
              disabled
              style={{ visibility: 'hidden', pointerEvents: 'none' }}
            >
              <IonIcon icon={ellipsisVertical} />
            </IonButton>
          </>
        }
        endButtons={
          <>
            <IonButton
              shape="round"
              className={styles.callButton}
              onClick={handleCallClick}
            >
              <IonIcon icon={callOutline} />
            </IonButton>
            <IonButton
              shape="round"
              className={styles.menuButton}
              onClick={() => setShowOptionsModal(true)}
            >
              <IonIcon icon={ellipsisVertical} />
            </IonButton>
          </>
        }
      >
        <IonContent className={styles.content}>
          {/* A tapped image, full screen in the thread under the same header. The messages stay mounted underneath, so
              closing it returns to exactly where you were. */}
          {viewingImage && selectedImage?.url && (
            <IonGrid slot="fixed" className={styles.threadImageView}>
              <img
                src={selectedImage.url}
                alt={selectedImage.name || ''}
                className={styles.threadImage}
                style={{ opacity: loadedImageUrl === selectedImage.url ? 1 : 0 }}
                onLoad={() => setLoadedImageUrl(selectedImage.url)}
                onError={() => setLoadedImageUrl(selectedImage.url)}
                onClick={closeImage}
              />
              {loadedImageUrl !== selectedImage.url && <IonSpinner name="bubbles" className={styles.threadImageSpinner} />}
            </IonGrid>
          )}
          {(threadLoading) ? (
            // The header bar (back, name + avatar, call, menu) is already showing (see the effect
            // above that keeps its title visible while there is no title card); the only loader is
            // the one for the messages. (There used to also be a separate full-screen loading
            // overlay, so two loaders appeared at once.)
            wasShortLastTime && conversationName ? (
              // A conversation known to fit on one screen: show its title card now, in the same place
              // and layout it has once the messages are in, so nothing moves or swaps when they arrive
              // (the header title stays hidden meanwhile — see the effect above).
              <IonGrid className={`${styles.messagesContainer} ${styles.chatScrollerContainer}`}>
                <div className={styles.chatScrollerTitleCard}>
                  <div className={styles.titleCardWrapper}>
                    <TitleCard
                      title={conversationName}
                      titleContainerClassName={styles.titleContainer}
                      titleAvatarClassName={styles.titleAvatar}
                      cardClassName={styles.inboxTitleCard}
                      titleClassName={styles.inboxTitle}
                      cardGradient="var(--app-page-bg)"
                      cardColor="var(--ion-text-color)"
                      avatarUrl={contactAvatarUrl}
                      avatarInitials={getInitials(conversationName)}
                    />
                  </div>
                </div>
                <div className={styles.loadingContainer}>
                  <IonSpinner name="bubbles" color="primary" />
                </div>
              </IonGrid>
            ) : (
              <div className={styles.loadingContainer}>
                {spinnerDue && <IonSpinner name="bubbles" color="primary" />}
              </div>
            )
          ) : allMessages.length > 0 ? (
            <ChatScroller
            ref={chatScrollerRef}
            messages={allMessages as unknown as Array<{ id?: string; [key: string]: unknown }>}
            conversationId={id}
            loadOlder={loadOlderAll}
            hasMore={hasMoreOlder}
            renderMessage={renderItem as unknown as (msg: { id?: string; [key: string]: unknown }, index: number, previousMsg?: { id?: string; [key: string]: unknown }) => React.ReactNode}
            containerClassName={styles.messagesContainer}
            isLoadingOlder={loadingOlder}
            loadingSpinner={loadingOlder ? <IonSpinner name="bubbles" color="primary" /> : undefined}
            onScroll={handleScroll}
            // Same distance the title card takes to collapse into the header: a list that scrolls by
            // less than this stays at the top, so its title card isn't clipped on open
            minScrollToPin={66}
            // The email channel adds a Subject row to the floating footer; give
            // the scroller extra bottom room so the last message clears it.
            bottomSpacerExtra={(isEmailChannel ? 56 : 0) + keyboardHeight}
            titleCard={
              !hasMoreOlder && !loadingOlder ? (
                <div ref={titleCardRef} className={styles.titleCardWrapper}>
                  <TitleCard
                    ref={titleCardAnimationRef}
                    title={conversationName}
                    titleContainerClassName={styles.titleContainer}
                    titleAvatarClassName={styles.titleAvatar}
                    cardClassName={styles.inboxTitleCard}
                    titleClassName={styles.inboxTitle}
                    cardGradient="var(--app-page-bg)"
                    cardColor="var(--ion-text-color)"
                    avatarUrl={contactAvatarUrl}
                    avatarInitials={getInitials(conversationName)}
                  />
                </div>
              ) : undefined
            }
          />
          ) : (
            <IonGrid className={styles.messagesContainer}>
              <IonRow>
                <IonCol size="12" className={styles.noMessagesCol}>
                  <IonText className={styles.noMessages}>
                    <p>{t('components.ConversationThread.noMessagesYet')}</p>
                  </IonText>
                </IonCol>
              </IonRow>
            </IonGrid>
          )}
        </IonContent>

        {/* Templates Modal */}
        <IonModal
          isOpen={showTemplatesModal}
          onDidDismiss={() => setShowTemplatesModal(false)}
          handle={false}
        >
          <TemplatesModal
            onDismiss={() => setShowTemplatesModal(false)}
            onSelectTemplate={(template) => {
              setMessage(template);
              setShowTemplatesModal(false);
            }}
          />
        </IonModal>

        {/* Add Content Modal */}
        <SheetModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          title={t('components.ConversationThread.addContent')}
          menuOptions={[
            {
              label: t('components.ConversationThread.photos'),
              icon: imageOutline,
              iconColor: 'primary',
              onClick: () => {
                setShowAddModal(false);
                // Handle photos
              }
            },
            {
              label: t('components.ConversationThread.files'),
              icon: documentOutline,
              iconColor: 'primary',
              onClick: () => {
                setShowAddModal(false);
                // Handle files
              }
            },
            {
              label: t('components.ConversationThread.coupons'),
              icon: ticketOutline,
              iconColor: 'primary',
              onClick: () => {
                setShowAddModal(false);
                // Handle coupons
              }
            },
            {
              label: t('components.ConversationThread.services'),
              icon: constructOutline,
              iconColor: 'primary',
              onClick: () => {
                setShowAddModal(false);
                // Handle services
              }
            },
            {
              label: t('components.ConversationThread.payLinks'),
              icon: cardOutline,
              iconColor: 'primary',
              onClick: () => {
                setShowAddModal(false);
                // Handle pay links
              }
            },
            {
              label: t('components.ConversationThread.pricePlans'),
              icon: pricetagOutline,
              iconColor: 'primary',
              onClick: () => {
                setShowAddModal(false);
                // Handle price plans
              }
            }
          ]}
        />

        {/* Options Modal */}
        <SheetModal
          isOpen={showOptionsModal}
          onClose={() => setShowOptionsModal(false)}
          title={viewingImage ? t('components.ConversationThread.photo') : t('components.ConversationThread.options')}
          menuOptions={viewingImage ? imageMenuOptions : [
            {
              label: t('components.ConversationThread.viewContact'),
              icon: personOutline,
              iconColor: 'primary',
              onClick: () => {
                setShowOptionsModal(false);
                if (contactId) onViewContact?.(contactId);
              }
            },
            {
              label: t('components.ConversationThread.createTask'),
              icon: checkmarkCircle,
              iconColor: 'primary',
              onClick: () => {
                setShowOptionsModal(false);
                // Handle create task
              }
            },
            {
              label: t('components.ConversationThread.starConversation'),
              icon: star,
              iconColor: 'warning',
              onClick: () => {
                setShowOptionsModal(false);
                // Handle star
              }
            },
            {
              label: t('components.ConversationThread.markUnread'),
              icon: mailOutline,
              iconColor: 'primary',
              onClick: () => {
                setShowOptionsModal(false);
                // Handle mark unread
              }
            },
            {
              label: t('components.ConversationThread.archiveConversation'),
              icon: archive,
              iconColor: 'primary',
              onClick: () => {
                setShowOptionsModal(false);
                // Saved on the conversation document (shared by all staff); the Inbox hides it and its Archived filter shows it
                if (id) {
                  updateDoc(doc(getFirestore(getApp(), 'wix-chat'), 'conversations', String(id)), { archived: true })
                    .then(() => onBack())
                    .catch((e: { code?: string }) => {
                      // not-found: no conversation document for this id (a contact who only has texts) — nothing to archive
                      if (e?.code !== 'not-found') console.error('Archiving the conversation failed:', e);
                    });
                }
              }
            },
            {
              label: t('components.ConversationThread.setPriority'),
              icon: flag,
              iconColor: 'primary',
              onClick: () => {
                setShowOptionsModal(false);
                // Handle set priority
              }
            },
            {
              label: t('components.ConversationThread.reportAndBlock'),
              icon: alertCircleOutline,
              iconColor: 'danger',
              onClick: () => {
                setShowOptionsModal(false);
                // Handle report and block
              }
            },
            {
              label: t('components.ConversationThread.deleteConversation'),
              icon: trashOutline,
              iconColor: 'danger',
              onClick: () => {
                setShowOptionsModal(false);
                // Handle delete
              }
            }
          ]}
        />
      </BasePageWithHeader>

      {/* Floating Input at Bottom */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => { handleFilesPicked(e.target.files); e.target.value = ''; }}
      />
      <ConversationInputFooter
        // The composer is hidden while an image is open full screen in the thread
        wrapperClassName={viewingImage ? styles.hidden : undefined}
        onKeyboardHeightChange={setKeyboardHeight}
        message={message}
        onMessageChange={setMessage}
        onSendClick={handleSend}
        onAddClick={() => setShowTemplatesModal(true)}
        onAttachClick={() => fileInputRef.current?.click()}
        attachments={footerAttachments}
        onRemoveAttachment={(key) => setComposerFiles(prev => prev.filter(f => f.key !== key))}
        attachDisabled={!isEmailChannel}
        channel={channel}
        onChannelChange={(newChannel) => {
          if (['chat', 'email', 'sms', 'gmail', 'outlook'].includes(newChannel)) {
            setChannel(newChannel as 'chat' | 'email' | 'sms' | 'gmail' | 'outlook' | 'yahoo' | 'other-email');
            localStorage.setItem('lastUsedChannel', newChannel);
          }
        }}
        isSending={false}
        canSend={true}
        subject={subject}
        onSubjectChange={setSubject}
        hasMessages={messages.length > 0}
        replySubject={allMessages.reduce<{ at: number; subject?: string }>((latest, m) => {
          const at = new Date(m._createdDate).getTime();
          // the parsed message carries an email's subject (messageParser), the shared Message type does not declare it
          const emailSubject = (m as { subject?: string }).subject;
          return m.channel === 'email' && emailSubject && at >= latest.at ? { at, subject: emailSubject } : latest;
        }, { at: -Infinity }).subject}
        fromEmail={fromEmail}
        onFromEmailChange={(account) => { setFromEmail(account); localStorage.setItem('lastUsedFromEmail', account); }}
      />
    </Page>
  );
};

export default ConversationThread;
